import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { FieldSectionType } from 'api/field-sections/types';
import { FieldsAPI } from 'api/fields/fields';
import { ObjectType } from 'api/pushes';
/**
 * Maps FieldSectionType to ObjectType for the getPaginatedFields API.
 */ const fieldSectionTypeToObjectType = {
    [FieldSectionType.Task]: ObjectType.Task,
    [FieldSectionType.Project]: ObjectType.Project,
    [FieldSectionType.ProjectPhase]: ObjectType.ProjectPhase,
    [FieldSectionType.Users]: ObjectType.Users,
    [FieldSectionType.Company]: ObjectType.Company,
    [FieldSectionType.Sprint]: ObjectType.Sprint,
    [FieldSectionType.Epic]: ObjectType.Epic,
    [FieldSectionType.TimeEntry]: ObjectType.TimeEntry,
    [FieldSectionType.Expense]: ObjectType.Expense,
    [FieldSectionType.Invoice]: ObjectType.Invoice,
    [FieldSectionType.Budget]: ObjectType.ProjectFinancialBudget,
    [FieldSectionType.Financial]: ObjectType.ProjectFinanicalField,
    [FieldSectionType.ResourceAllocation]: ObjectType.ResourceAllocation
};
/**
 * Query key prefix for settings section fields.
 * Used by mutations to invalidate all settings section field queries.
 */ export const SETTINGS_SECTION_FIELDS_QUERY_KEY = 'settings-section-fields';
export const getSettingsSectionFieldsQueryKey = (fieldSectionType, fieldSectionId, fieldSectionSubType)=>[
        SETTINGS_SECTION_FIELDS_QUERY_KEY,
        fieldSectionType,
        fieldSectionId,
        fieldSectionSubType
    ];
/**
 * Hook for paginated field loading within a specific field section for settings pages.
 *
 * Uses React Query's `useInfiniteQuery` for caching and automatic invalidation.
 *
 * Unlike `usePaginatedSectionFields`, this hook:
 * - Does NOT filter by `isEnabled` (settings pages show all fields)
 * - Does NOT hardcode project-specific filters
 * - Accepts an optional `fieldFilter` callback for page-specific filtering
 */ export const useSettingsSectionFields = ({ fieldSectionId, fieldSectionType, fieldSectionSubType, pageSize = 50, enabled = true, fieldFilter, fieldOrderIds })=>{
    var _ref;
    var _data_pages_;
    const objectType = fieldSectionTypeToObjectType[fieldSectionType];
    const queryKey = [
        ...getSettingsSectionFieldsQueryKey(fieldSectionType, fieldSectionId, fieldSectionSubType),
        pageSize,
        fieldOrderIds !== null && fieldOrderIds !== void 0 ? fieldOrderIds : null
    ];
    const useOrderedPagination = fieldOrderIds !== undefined && fieldOrderIds.length > 0;
    const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam = 0 })=>{
            if (useOrderedPagination) {
                const pageIndex = pageParam;
                const start = pageIndex * pageSize;
                const idsForPage = fieldOrderIds.slice(start, start + pageSize);
                return FieldsAPI.getPaginatedFields(objectType, {
                    includedFieldIds: idsForPage,
                    objectSubType: fieldSectionSubType,
                    limit: idsForPage.length
                });
            }
            return FieldsAPI.getPaginatedFields(objectType, {
                limit: pageSize,
                ...pageParam ? {
                    lastRecordPointer: pageParam
                } : {
                    offset: 0
                },
                fieldSectionId,
                objectSubType: fieldSectionSubType
            });
        },
        getNextPageParam: (lastPage, allPages)=>{
            var _lastPage_nextPage;
            if (useOrderedPagination) {
                const loadedCount = allPages.length * pageSize;
                return loadedCount < fieldOrderIds.length ? allPages.length : undefined;
            }
            const offset = (_lastPage_nextPage = lastPage.nextPage) === null || _lastPage_nextPage === void 0 ? void 0 : _lastPage_nextPage.offset;
            if (offset !== undefined && offset !== null && offset > 0) {
                return offset;
            }
            return undefined;
        },
        enabled: enabled && !!objectType,
        refetchOnWindowFocus: false
    });
    const fields = useMemo(()=>{
        var _ref;
        const allFields = (_ref = data === null || data === void 0 ? void 0 : data.pages.flatMap((page)=>page.data)) !== null && _ref !== void 0 ? _ref : [];
        return fieldFilter ? allFields.filter(fieldFilter) : allFields;
    }, [
        data,
        fieldFilter
    ]);
    useEffect(()=>{
        if (fieldFilter && fields.length === 0 && hasNextPage && !isFetchingNextPage && !isLoading) {
            fetchNextPage();
        }
    }, [
        fields.length,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        fieldFilter,
        fetchNextPage
    ]);
    const totalCount = (_ref = data === null || data === void 0 ? void 0 : (_data_pages_ = data.pages[0]) === null || _data_pages_ === void 0 ? void 0 : _data_pages_.count) !== null && _ref !== void 0 ? _ref : 0;
    return {
        fields,
        loading: isLoading && !!objectType,
        isLoadingMore: isFetchingNextPage,
        loadNextPage: fetchNextPage,
        hasMore: hasNextPage !== null && hasNextPage !== void 0 ? hasNextPage : false,
        totalCount
    };
};
